<template>

    <div class="coupon-list mt-3 mx-2" v-if="coupons && coupons.length">
        <!-- Coupons -->
        <div v-for="coupon in coupons" :key="coupon.id" class="coupon-card"
            :class="{ 'is-open': openCoupon === coupon.id }">

            <!-- Header -->
            <div class="coupon-header-row" @click="toggleCoupon(coupon.id)">

                <div class="coupon-icon">
                    <i class="bi bi-ticket-perforated"></i>
                </div>


                <div class="coupon-content">

                    <div class="coupon-title">
                        {{ coupon.name }}
                    </div>

                    <div class="coupon-info">

                        <span class="coupon-discount">
                            {{ getDiscount(coupon) }}
                        </span>

                        <span v-if="getMainCondition(coupon)" class="coupon-condition">
                            {{ getMainCondition(coupon) }}
                        </span>

                    </div>

                </div>


                <div class="coupon-meta">

                    <span class="coupon-auto">

                        <i class="bi bi-check-circle-fill"></i>

                        خودکار

                    </span>

                    <span class="coupon-expire">
                        {{ getExpireText(coupon) }}
                    </span>

                </div>


                <div class="coupon-arrow">

                    <i class="bi bi-chevron-down"></i>

                </div>

            </div>


            <!-- Details -->

            <Transition name="coupon-slide">

                <div v-if="openCoupon === coupon.id" class="coupon-details">

                    <!-- Description -->

                    <div class="coupon-description">

                        {{ coupon.description }}

                    </div>


                    <!-- Coupon Details -->

                    <div class="coupon-detail-list">


                        <!-- Discount -->

                        <div class="coupon-detail-item">

                            <span class="detail-label">
                                میزان تخفیف
                            </span>

                            <span class="detail-value">
                                {{ getDiscount(coupon) }}
                            </span>

                        </div>

                        <!-- Minimum Purchase -->

                        <div v-for="rule in getRules(coupon, 'minimum_purchase')" :key="rule.id"
                            class="coupon-detail-item">

                            <span class="detail-label">
                                حداقل خرید
                            </span>

                            <span class="detail-value">
                                {{ formatPrice(rule.value) }}
                            </span>

                        </div>


                        <!-- First Order -->

                        <div class="coupon-detail-item" v-if="coupon.is_first_order == 1">

                            <span class="detail-label">
                                سفارش اول
                            </span>

                            <span class="detail-value">

                                {{
                                    Number(coupon.is_first_order) === 1
                                        ? 'فقط سفارش اول'
                                        : 'بدون محدودیت'
                                }}

                            </span>

                        </div>


                        <!-- Start -->

                        <div class="coupon-detail-item">

                            <span class="detail-label">
                                شروع
                            </span>

                            <span class="detail-value">
                                {{ formatDate(coupon.starts_at) }}
                            </span>

                        </div>


                        <!-- Expire -->

                        <div class="coupon-detail-item">

                            <span class="detail-label">
                                انقضا
                            </span>

                            <span class="detail-value">
                                {{ formatDate(coupon.expires_at) }}
                            </span>

                        </div>


                        <!-- Other Rules -->

                        <div v-for="rule in getVisibleRules(coupon)" :key="rule.id" class="coupon-detail-item">

                            <span class="detail-label">
                                {{ getRuleName(rule.rule_type) }}
                            </span>

                            <span class="detail-value">
                                {{ getRuleValue(rule) }}
                            </span>

                        </div>

                    </div>


                    <!-- Note -->

                    <div class="coupon-note">

                        <i class="bi bi-info-circle"></i>

                        <span>
                            این تخفیف در صورت داشتن شرایط،
                            به صورت خودکار روی سبد خرید اعمال می‌شود.
                        </span>

                    </div>

                </div>

            </Transition>

        </div>

    </div>

</template>


<script setup>

import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'


const route = useRoute()


const coupons = ref([])

const openCoupon = ref(null)


/*
|--------------------------------------------------------------------------
| Toggle Coupon
|--------------------------------------------------------------------------
*/

const toggleCoupon = (id) => {

    if (openCoupon.value === id) {

        openCoupon.value = null

    } else {

        openCoupon.value = id

    }

}


/*
|--------------------------------------------------------------------------
| Get Coupons
|--------------------------------------------------------------------------
*/

const getCoupons = async () => {

    try {

        const response = await fetch(
            `https://radap.ir/Backend/api/section/pages/store/coupons.php?id=${route.params.id}`
        )

        const data = await response.json()


        if (data.status) {

            coupons.value = data.data || []

        } else {

            coupons.value = []

        }

    } catch (error) {

        console.error('Coupon Error:', error)

        coupons.value = []

    }

}


onMounted(() => {

    getCoupons()

})


/*
|--------------------------------------------------------------------------
| Discount
|--------------------------------------------------------------------------
*/

const getDiscount = (coupon) => {

    switch (coupon.discount_type) {

        case 'percentage':

            return `${parseInt(coupon.discount_value)}٪ تخفیف`


        case 'fixed':

        case 'fixed_amount':

            return `${formatPrice(parseInt(coupon.discount_value))} تخفیف`


        case 'free_shipping':

            return 'ارسال رایگان'


        default:

            return coupon.discount_value

    }

}


/*
|--------------------------------------------------------------------------
| Discount Type
|--------------------------------------------------------------------------
*/

const getDiscountType = (type) => {

    const types = {

        percentage: 'درصدی',

        fixed: 'مبلغ ثابت',

        fixed_amount: 'مبلغ ثابت',

        free_shipping: 'ارسال رایگان'

    }

    return types[type] || type

}


/*
|--------------------------------------------------------------------------
| Main Condition
|--------------------------------------------------------------------------
*/

const getMainCondition = (coupon) => {

    if (!coupon.rules) {

        return ''

    }


    const rule = coupon.rules.find(
        rule => rule.rule_type === 'minimum_purchase'
    )


    if (rule) {

        return `حداقل خرید ${formatPrice(rule.value)}`

    }


    return ''

}


/*
|--------------------------------------------------------------------------
| Get Rules
|--------------------------------------------------------------------------
*/

const getRules = (coupon, type) => {

    if (!coupon.rules) {

        return []

    }


    return coupon.rules.filter(
        rule => rule.rule_type === type
    )

}


/*
|--------------------------------------------------------------------------
| Visible Rules
|--------------------------------------------------------------------------
|
| supplier و minimum_purchase نمایش داده نمی‌شوند
|
*/

const getVisibleRules = (coupon) => {

    if (!coupon.rules) {

        return []

    }


    return coupon.rules.filter(rule =>

        rule.rule_type !== 'supplier' &&
        rule.rule_type !== 'minimum_purchase'

    )

}


/*
|--------------------------------------------------------------------------
| Rule Name
|--------------------------------------------------------------------------
*/

const getRuleName = (type) => {

    const names = {

        supplier: 'تأمین‌کننده',

        group: 'گروه',

        main_group: 'گروه اصلی',

        ordination: 'دسته‌بندی',

        minimum_purchase: 'حداقل خرید',

        user: 'کاربر',

        day_of_week: 'روز هفته'

    }


    return names[type] || type

}


/*
|--------------------------------------------------------------------------
| Rule Value
|--------------------------------------------------------------------------
*/

const getRuleValue = (rule) => {

    /*
    |--------------------------------------------------------------------------
    | Minimum Purchase
    |--------------------------------------------------------------------------
    */

    if (rule.rule_type === 'minimum_purchase') {

        return formatPrice(rule.value)

    }


    /*
    |--------------------------------------------------------------------------
    | Week Day
    |--------------------------------------------------------------------------
    */

    if (rule.rule_type === 'day_of_week') {

        const days = {

            saturday: 'شنبه',

            sunday: 'یکشنبه',

            monday: 'دوشنبه',

            tuesday: 'سه‌شنبه',

            wednesday: 'چهارشنبه',

            thursday: 'پنجشنبه',

            friday: 'جمعه'

        }


        return days[rule.value] || rule.value

    }


    /*
    |--------------------------------------------------------------------------
    | Other Rules
    |--------------------------------------------------------------------------
    */

    return rule.value

}


/*
|--------------------------------------------------------------------------
| Format Price
|--------------------------------------------------------------------------
*/

const formatPrice = (price) => {

    if (
        price === null ||
        price === undefined ||
        price === ''
    ) {

        return '-'

    }


    return new Intl.NumberFormat('fa-IR').format(
        Number(price)
    ) + ' تومان'

}


/*
|--------------------------------------------------------------------------
| Format Date
|--------------------------------------------------------------------------
*/

const formatDate = (date) => {

    if (!date) {

        return '-'

    }


    return date
        .split(' ')[0]
        .replaceAll('-', '/')

}


/*
|--------------------------------------------------------------------------
| Expire Text
|--------------------------------------------------------------------------
*/

const getExpireText = (coupon) => {

    if (!coupon.expires_at) {

        return 'بدون تاریخ انقضا'

    }


    return `تا ${formatDate(coupon.expires_at)}`

}

</script>


<style scoped>
.coupon-list {
    direction: rtl;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 10px;

}


/* =========================
   Card
========================= */

.coupon-card {

    min-width: 0;

    background: #fff;

    border: 1px solid #eeeeee;

    border-radius: 10px;

    overflow: hidden;

    transition: .2s ease;

}


.coupon-card:hover {

    border-color: #ddd;

}


.coupon-card.is-open {

    border-color: #ddd;

}


/* =========================
   Header
========================= */

.coupon-header-row {

    min-height: 72px;

    display: flex;

    align-items: center;

    padding: 10px 12px;

    cursor: pointer;

}


/* =========================
   Icon
========================= */

.coupon-icon {

    width: 40px;

    height: 40px;

    min-width: 40px;

    display: flex;

    align-items: center;

    justify-content: center;

    margin-left: 10px;

    border-radius: 8px;

    background: #fff3f3;

    color: #ff3737;

    font-size: 17px;

}


/* =========================
   Content
========================= */

.coupon-content {

    flex: 1;

    min-width: 0;

}


.coupon-title {

    font-size: 13px;

    font-weight: 700;

    color: #222;

    line-height: 1.6;

    margin-bottom: 5px;

}


.coupon-info {

    display: flex;

    align-items: center;

    gap: 10px;

    font-size: 11px;

}


.coupon-discount {

    color: #ff3737;

    font-weight: 700;

    white-space: nowrap;

}


.coupon-condition {

    color: #888;

    white-space: normal;

    line-height: 1.6;

}


/* =========================
   Meta
========================= */

.coupon-meta {

    display: flex;

    flex-direction: column;

    align-items: flex-end;

    gap: 5px;

    margin-right: 10px;

    font-size: 10px;

}


.coupon-auto {

    display: flex;

    align-items: center;

    gap: 3px;

    color: #198754;

}


.coupon-auto svg {

    font-size: 9px;

}


.coupon-expire {

    color: #999;

    white-space: nowrap;

}


/* =========================
   Arrow
========================= */

.coupon-arrow {

    width: 25px;

    display: flex;

    align-items: center;

    justify-content: center;

    color: #aaa;

    font-size: 11px;

    transition: .2s ease;

}


.coupon-card.is-open .coupon-arrow {

    transform: rotate(180deg);

    color: #555;

}


/* =========================
   Details
========================= */

.coupon-details {

    border-top: 1px solid #f1f1f1;

    padding: 12px 15px 14px;

    background: #fafafa;

}


.coupon-description {

    color: #666;

    font-size: 11px;

    line-height: 1.8;

    margin-bottom: 12px;

}


/* =========================
   Detail List
========================= */

.coupon-detail-list {

    display: grid;

    grid-template-columns: repeat(2, 1fr);

    gap: 8px;

}


.coupon-detail-item {

    display: flex;

    align-items: center;

    justify-content: space-between;

    gap: 10px;

    padding: 7px 9px;

    background: #fff;

    border-radius: 6px;

    font-size: 10px;

}


.detail-label {

    color: #999;

}


.detail-value {

    color: #333;

    font-weight: 600;

}


/* =========================
   Note
========================= */

.coupon-note {

    display: flex;

    align-items: flex-start;

    gap: 6px;

    margin-top: 10px;

    padding: 8px 10px;

    background: #f1faf4;

    color: #198754;

    border-radius: 6px;

    font-size: 10px;

    line-height: 1.7;

}


.coupon-note svg {

    margin-top: 3px;

}


/* =========================
   Animation
========================= */

.coupon-slide-enter-active,
.coupon-slide-leave-active {

    transition:
        max-height .3s ease,
        opacity .2s ease;

    overflow: hidden;

}


.coupon-slide-enter-from,
.coupon-slide-leave-to {

    max-height: 0;

    opacity: 0;

}


.coupon-slide-enter-to,
.coupon-slide-leave-from {

    max-height: 700px;

    opacity: 1;

}


/* =========================
   Tablet / Mobile
========================= */

@media (max-width: 768px) {

    .coupon-list {

        grid-template-columns: 1fr;

    }

}


@media (max-width: 576px) {

    .coupon-header-row {

        min-height: 68px;

        padding: 8px 10px;

    }


    .coupon-icon {

        width: 36px;

        height: 36px;

        min-width: 36px;

        margin-left: 8px;

        font-size: 15px;

    }


    .coupon-title {

        font-size: 12px;

    }


    .coupon-info {

        font-size: 10px;

        gap: 7px;

    }


    .coupon-meta {

        margin-right: 7px;

        font-size: 9px;

    }


    .coupon-detail-list {

        grid-template-columns: 1fr;

    }

}
</style>